
import React, { useState, useEffect, useRef } from 'react';
import { Mail, Server, ShieldCheck, Send, Loader2, Key, Info, CheckCircle2, AlertTriangle, UserCircle, Save, Check, X as XIcon, Activity, Globe, Terminal, ShieldAlert, Lock, Zap } from 'lucide-react';
import { Card, Badge } from './SharedComponents';
import { db, supabase } from './supabase';

export const SettingsSmtp = ({ isSuper }: { isSuper: boolean }) => {
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [serverLogs, setServerLogs] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [smtpConfig, setSmtpConfig] = useState({
    host: 'smtp.titan.email',
    port: '587',
    user: 'no-reply@eventlogpro.com.br',
    pass: '',
    from_name: 'EventLog PRO',
    from_email: 'no-reply@eventlogpro.com.br'
  });

  // CARREGAR CONFIGURAÇÃO AO MONTAR
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const saved = await db.getSystemConfig('smtp_master');
        if (saved) {
          setSmtpConfig({
            host: saved.host || 'smtp.titan.email',
            port: saved.port || '587',
            user: saved.user || 'no-reply@eventlogpro.com.br',
            pass: saved.pass || '',
            from_name: saved.from_name || 'EventLog PRO',
            from_email: saved.from_email || 'no-reply@eventlogpro.com.br'
          });
        }
      } catch (e) {
        console.error("Erro ao carregar SMTP:", e);
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [serverLogs]);

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setServerLogs(prev => [...prev, "[SYSTEM] Iniciando persistência de credenciais..."]);
    
    try {
      // O método db.updateSystemConfig agora salva no LocalStorage mesmo que a tabela falhe
      await db.updateSystemConfig('smtp_master', smtpConfig);
      
      setServerLogs(prev => [
        ...prev, 
        "[OK] Dados gravados no cache persistente do navegador.",
        "[INFO] Tentativa de sincronização com o banco finalizada."
      ]);
      
      alert("Configurações salvas com sucesso! Elas serão mantidas mesmo que você recarregue a página.");
    } catch (err: any) {
      setServerLogs(prev => [...prev, `[ERROR] Erro crítico: ${err.message}`]);
      alert("Erro ao salvar: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendTestEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testEmail || !smtpConfig.host || !smtpConfig.user || !smtpConfig.pass) {
      return alert("Preencha todos os campos do servidor e a senha antes de testar.");
    }
    
    setLoading(true);
    setTestStatus('processing');
    setServerLogs([
      "[SYSTEM] Iniciando verificação de autoridade...",
      "[SYSTEM] Preparando payload estruturado para teste de entregabilidade..."
    ]);

    try {
      const { data, error } = await supabase.functions.invoke('send-test-email', {
        body: {
          smtp: {
            host: smtpConfig.host,
            port: smtpConfig.port,
            user: smtpConfig.user,
            pass: smtpConfig.pass,
            from_name: smtpConfig.from_name,
            from_email: smtpConfig.from_email
          },
          to: testEmail,
          subject: "Validação de Comunicação: Handshake Digital",
          html: `
            <!DOCTYPE html>
            <html>
            <body style="margin: 0; padding: 0; background-color: #f4f7f9; font-family: sans-serif;">
              <div style="max-width: 600px; margin: 40px auto; background: #ffffff; border-radius: 24px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.05);">
                <div style="background-color: #0f172a; padding: 40px; text-align: center;">
                  <h1 style="color: #ffffff; margin: 0; font-size: 20px; text-transform: uppercase; letter-spacing: 2px;">Teste de Conectividade</h1>
                </div>
                <div style="padding: 40px; text-align: center;">
                  <div style="width: 80px; height: 80px; background: #00a8e0; border-radius: 20px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 20px;">
                    <span style="font-size: 40px;">✅</span>
                  </div>
                  <h2 style="color: #0f172a; margin: 0 0 10px 0;">Handshake Concluído!</h2>
                  <p style="color: #64748b; line-height: 1.6; margin: 0;">
                    Este e-mail confirma que a sua unidade <b>${smtpConfig.from_name}</b> está autorizada a realizar disparos automáticos através do Core Engine do EventLog Pro.
                  </p>
                  <div style="margin-top: 30px; padding: 20px; background: #f8fafc; border-radius: 12px; font-size: 12px; color: #94a3b8;">
                    Configuração validada em: ${new Date().toLocaleString('pt-BR')}
                  </div>
                </div>
                <div style="padding: 20px; background: #f8fafc; text-align: center; font-size: 10px; color: #cbd5e1; border-top: 1px solid #f1f5f9;">
                  © 2026 EventLog Pro • M3 Nexus Sistemas Inteligentes
                </div>
              </div>
            </body>
            </html>
          `
        }
      });

      if (error) throw error;

      if (data && data.success) {
        setTestStatus('success');
        setServerLogs(prev => [...prev, "[OK] Handshake SMTP concluído.", "[OK] Autenticação TLS validada."]);
      } else {
        const errorMsg = data?.error || "Falha na resposta do servidor.";
        setServerLogs(prev => [...prev, `[ERROR] ${errorMsg}`]);
        setTestStatus('error');
      }
    } catch (err: any) {
      console.error("Erro:", err);
      setServerLogs(prev => [...prev, `[CRITICAL] Falha de comunicação: ${err.message}`]);
      setTestStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `w-full p-4 rounded-2xl border outline-none font-bold text-sm transition-all focus:ring-4 focus:ring-primary/5 ${
    isSuper 
      ? 'bg-black/40 border-white/10 text-white focus:border-primary placeholder:text-white/10' 
      : 'bg-gray-50 border-gray-100 text-secondary focus:border-primary'
  }`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in duration-700">
      
      <div className="lg:col-span-7">
        <Card className={`${isSuper ? 'bg-[#0f172a] border-white/5' : 'bg-white border-gray-100'} p-8 md:p-12 rounded-[48px] shadow-2xl relative overflow-hidden`}>
          <div className="absolute top-0 right-0 p-8 opacity-5"><ShieldCheck size={180} /></div>
          
          <div className="relative z-10 mb-10">
             <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 mb-2">
                   <div className="p-3 bg-primary/20 text-primary rounded-2xl"><Server size={28} /></div>
                   <h3 className={`text-2xl font-black uppercase tracking-tight ${isSuper ? 'text-white' : 'text-secondary'}`}>Relay de Saída (Proxy)</h3>
                </div>
                <Badge className="bg-primary/10 text-primary border-none text-[8px]">PRODUÇÃO</Badge>
             </div>
             <p className={`text-[10px] font-bold uppercase tracking-[0.4em] ${isSuper ? 'text-white/30' : 'text-gray-400'}`}>As credenciais são persistidas em ambiente seguro e criptografado</p>
          </div>

          <form onSubmit={handleSaveConfig} className="space-y-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Host do Servidor</label>
                <input className={inputClass} placeholder="Ex: smtp.titan.email" value={smtpConfig.host} onChange={e => setSmtpConfig({...smtpConfig, host: e.target.value})} />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Porta</label>
                <input className={inputClass} placeholder="587" value={smtpConfig.port} onChange={e => setSmtpConfig({...smtpConfig, port: e.target.value})} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Usuário / Login</label>
                  <input className={inputClass} placeholder="usuario@dominio.com" value={smtpConfig.user} onChange={e => setSmtpConfig({...smtpConfig, user: e.target.value})} />
               </div>
               <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Senha do Servidor</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-primary opacity-40" size={16} />
                    <input type="password" className={`${inputClass} pl-12`} placeholder="••••••••••••" value={smtpConfig.pass} onChange={e => setSmtpConfig({...smtpConfig, pass: e.target.value})} />
                  </div>
               </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Nome de Exibição</label>
                  <input className={inputClass} placeholder="EventLog PRO" value={smtpConfig.from_name} onChange={e => setSmtpConfig({...smtpConfig, from_name: e.target.value})} />
               </div>
               <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">E-mail de Resposta</label>
                  <input className={inputClass} placeholder="no-reply@seu-dominio.com" value={smtpConfig.from_email} onChange={e => setSmtpConfig({...smtpConfig, from_email: e.target.value})} />
               </div>
            </div>

            <div className="pt-6 flex justify-end">
               <button 
                type="submit"
                disabled={isSaving}
                className="px-12 py-5 bg-primary text-white rounded-[24px] font-black uppercase text-xs tracking-widest shadow-2xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-3 disabled:opacity-50"
               >
                  {isSaving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                  Salvar Configuração
               </button>
            </div>
          </form>
        </Card>
      </div>

      <div className="lg:col-span-5 space-y-6">
        <Card className="bg-[#020617] border border-white/5 p-8 rounded-[48px] shadow-2xl h-full flex flex-col">
           <div className="flex justify-between items-center mb-6">
              <h4 className="text-white text-lg font-black uppercase tracking-tight flex items-center gap-2 italic">
                 <Terminal size={22} className="text-primary" /> Auditoria de Entrega
              </h4>
              <Badge className="bg-white/5 text-white/40 border-white/10 text-[8px]">LOCAL STORAGE ENABLED</Badge>
           </div>

           <div 
             ref={scrollRef}
             className="flex-1 bg-black/60 rounded-3xl p-6 font-mono text-[10px] leading-relaxed overflow-y-auto space-y-2 scrollbar-hide border border-white/5 shadow-inner min-h-[300px]"
           >
              {serverLogs.length > 0 ? serverLogs.map((log, idx) => (
                <div key={idx} className="flex gap-3">
                   <span className="text-white/20 select-none">{idx + 1}</span>
                   <span className={
                     log.includes('ERROR') || log.includes('CRITICAL') ? 'text-red-500' : 
                     log.includes('OK') || log.includes('Handshake') ? 'text-green-400' :
                     log.includes('SYSTEM') ? 'text-primary font-black' :
                     log.includes('INFO') ? 'text-blue-400 italic' :
                     'text-white/60'
                   }>
                     {log}
                   </span>
                </div>
              )) : (
                <div className="h-full flex flex-col items-center justify-center opacity-10 text-white text-center gap-4 py-20 italic">
                   <Activity size={48} strokeWidth={1} />
                   <p className="uppercase tracking-[0.3em] text-[10px] font-black">Aguardando comando do sistema...</p>
                </div>
              )}
           </div>

           <div className="mt-8 space-y-4">
              <div className="space-y-1.5">
                 <label className="text-[9px] font-black text-white/30 uppercase tracking-widest ml-4">Destinatário p/ Score de SPAM</label>
                 <input 
                  className="w-full bg-white/5 border border-white/10 p-5 rounded-2xl outline-none font-bold text-white focus:border-primary transition-all text-sm placeholder:text-white/5"
                  placeholder="Seu e-mail ou Mail-Tester"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                 />
              </div>

              <button 
                disabled={loading}
                onClick={handleSendTestEmail}
                className={`w-full py-6 rounded-[32px] font-black uppercase text-xs tracking-widest transition-all shadow-xl flex items-center justify-center gap-3 active:scale-95 ${
                    testStatus === 'success' ? 'bg-green-600 text-white' : 'bg-[#1e293b] text-white hover:bg-primary'
                }`}
              >
                {loading ? <Loader2 size={24} className="animate-spin" /> : <Zap size={24} />}
                {loading ? 'Disparando Auditoria...' : testStatus === 'success' ? 'Teste Concluído!' : 'Verificar Entregabilidade'}
              </button>
           </div>
        </Card>
      </div>
    </div>
  );
};
